import { Link } from "react-router-dom";
import { Phone, MapPin, ArrowRight } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { imgUrl } from "../lib/api";
import { TEL, WA, PHONE_DISPLAY, MAPS_DIR } from "../lib/site";
import { Seo } from "../components/site/Seo";
import { WhatsAppIcon } from "../components/site/Icons";
import { DiscountBadge, ServiceGrid } from "../components/site/ServiceCard";
import { SectionTitle } from "../components/site/SectionTitle";
import { CampaignBanner } from "../components/site/CampaignBanner";
import { WhySection } from "../components/site/WhySection";
import { ReviewsCarousel } from "../components/site/ReviewsCarousel";
import { GiftTeaser } from "../components/site/GiftTeaser";
import { GalleryGrid } from "../components/site/GalleryGrid";
import { InstagramSection } from "../components/site/InstagramSection";
import { ContactSection } from "../components/site/ContactSection";

const Hero = () => {
  const { settings } = useSite();
  return (
    <section className="relative isolate overflow-hidden bg-emerald-900" data-testid="hero-section">
      <img src={imgUrl(settings.hero_url)} alt="lunessa-sivas-masaj-salonu-spa" className="absolute inset-0 -z-10 h-full w-full object-cover" width="1600" height="1070" fetchPriority="high" />
      <div className="absolute inset-0 -z-10" style={{ background: "linear-gradient(100deg, rgba(2,44,34,0.86) 0%, rgba(4,78,59,0.62) 48%, rgba(6,78,59,0.18) 100%)" }} />
      <div className="container-lux flex min-h-[78vh] flex-col justify-center py-20 text-white lg:min-h-[86vh]">
        <div className="fade-up max-w-2xl">
          <DiscountBadge testId="hero-badge" />
          <h1 className="mt-6 text-4xl font-bold leading-[1.08] sm:text-5xl lg:text-6xl" data-testid="hero-h1">
            Sivas Masaj Salonu <span className="block font-semibold text-gold-light">– Lunessa Spa Masaj</span>
          </h1>
          <p className="mt-5 text-base font-medium text-white/90 md:text-lg" data-testid="hero-sub">Sivas’ta Profesyonel Masaj ve Spa Deneyimi</p>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/75">
            Lunessa Spa Masaj, Sivas’ta konforlu atmosferi, profesyonel hizmet anlayışı ve özenli masaj deneyimiyle günlük yaşamın yoğunluğundan uzaklaşmak isteyen misafirlerine özel bir ortam sunar.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a href={TEL} className="btn-ghost-light" data-testid="hero-call-btn"><Phone className="h-4 w-4" /> Hemen Ara</a>
            <a href={WA} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="hero-whatsapp-btn"><WhatsAppIcon className="h-4 w-4" /> WhatsApp’tan Randevu Al</a>
            <Link to="/masaj-hizmetleri" className="btn-base text-white/90 underline decoration-gold/70 underline-offset-8 hover:text-gold-light" data-testid="hero-services-btn">
              Hizmetleri İncele <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const QUICK = [
  { icon: Phone, label: "Telefon", value: PHONE_DISPLAY, href: TEL, id: "phone" },
  { icon: WhatsAppIcon, label: "WhatsApp", value: "Randevu Al", href: WA, id: "whatsapp", ext: true },
  { icon: MapPin, label: "Konum", value: "Merkez / Sivas", href: MAPS_DIR, id: "location", ext: true },
];

const QuickBar = () => (
  <div className="container-lux relative z-10 -mt-10 lg:-mt-12" data-testid="quick-contact-bar">
    <div className="grid divide-y divide-gold/15 overflow-hidden rounded-2xl border border-gold/25 bg-white shadow-[0_24px_50px_-28px_rgba(2,44,34,0.45)] sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      {QUICK.map((q) => (
        <a key={q.id} href={q.href} {...(q.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="group flex items-center gap-4 px-6 py-5 transition-colors hover:bg-cream" data-testid={`quick-${q.id}`}>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-gold-light"><q.icon className="h-[18px] w-[18px]" strokeWidth={1.8} /></span>
          <span>
            <span className="block text-[11px] uppercase tracking-[0.2em] text-ink/50">{q.label}</span>
            <span className="block font-semibold text-emerald-950 transition-colors group-hover:text-gold-deep">{q.value}</span>
          </span>
        </a>
      ))}
    </div>
  </div>
);

const About = () => (
  <section className="container-lux grid items-center gap-12 py-20 lg:grid-cols-2 lg:gap-20 lg:py-28" data-testid="about-section">
    <div className="relative order-2 lg:order-1">
      <div className="absolute -left-3 -top-3 h-full w-full rounded-3xl border border-gold/40 sm:-left-5 sm:-top-5" />
      <img src="/images/lunessa-sivas-spa-salonu-dis-cephe.webp" alt="lunessa-sivas-masaj-salonu-dis-cephe" loading="lazy" width="1600" height="900" className="relative aspect-[4/3] w-full rounded-3xl object-cover shadow-[0_30px_60px_-30px_rgba(2,44,34,0.5)]" />
    </div>
    <div className="order-1 lg:order-2">
      <SectionTitle eyebrow="Hakkımızda" title="Lunessa Spa Masaj Sivas" />
      <p className="-mt-4 text-base leading-[1.85] text-ink/70">
        Lunessa Spa Masaj, Sivas Merkez’de masaj ve spa hizmetlerinde konforu, hijyeni ve profesyonel hizmet anlayışını bir araya getirir. Günlük yaşamın stresinden uzaklaşabileceğiniz, kendinize zaman ayırabileceğiniz ve rahatlayabileceğiniz özel bir ortam sunuyoruz. Misafir memnuniyeti, hijyen ve kaliteli hizmet anlayışı Lunessa’nın temel öncelikleridir.
      </p>
      <Link to="/hakkimizda" className="btn-emerald mt-8" data-testid="about-discover-btn">Lunessa’yı Keşfet <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </section>
);

export default function Home() {
  const { services, gallery } = useSite();
  return (
    <>
      <Seo
        title="Sivas Masaj Salonu | Lunessa Spa Masaj"
        description="Sivas masaj salonu Lunessa Spa Masaj’da profesyonel masaj ve spa hizmetleriyle rahatlayın. Sivas’ta masaj deneyimi ve randevu için hemen iletişime geçin."
        path="/"
      />
      <Hero />
      <QuickBar />
      <About />
      <CampaignBanner />
      <section className="container-lux py-20 lg:py-28" data-testid="home-services-section">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <SectionTitle eyebrow="Hizmetler" title="Masaj Hizmetlerimiz" sub="Kendinize zaman ayırın, Lunessa’nın rahatlatıcı masaj deneyimiyle yenilenin." />
          <Link to="/masaj-hizmetleri" className="btn-outline mb-10 shrink-0 self-start sm:self-auto lg:mb-14" data-testid="home-all-services-btn">Tüm Hizmetler <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <ServiceGrid services={services} />
      </section>
      <WhySection />
      <ReviewsCarousel />
      <GiftTeaser />
      <section className="container-lux py-20 lg:py-28" data-testid="home-gallery-section">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <SectionTitle eyebrow="Galeri" title="Lunessa’dan Kareler" sub="Salonumuzun sakin atmosferinden birkaç kare." />
          <Link to="/galeri" className="btn-outline mb-10 shrink-0 self-start sm:self-auto lg:mb-14" data-testid="home-gallery-btn">Tüm Galeri <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <GalleryGrid items={gallery.slice(0, 6)} />
      </section>
      <div className="bg-cream-grad">
        <InstagramSection />
      </div>
      <ContactSection />
    </>
  );
}
